import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";

import { useAssignmentsGet } from "@/assignments/useAssignmentsGet";
import useAssignmentViewRecordCreate from "@/assignments/useAssignmentViewRecordCreate";
import useLazyAssignmentGet from "@/assignments/useLazyAssignmentGet";
import { getIsSavedAssignment } from "@/assignments/utils/assignmentUrlHelpers";
import { getLatestAssignment } from "@/assignments/utils/getLatestAssignment";
import { recordStudentAssignmentView } from "@/assignments/utils/recordStudentAssignmentView";
import useContentMetadataGet from "@/content/useContentMetadataGet";
import { UseRestQueryResponse } from "@/remoteState/useRestQuery";
import { BannerMode } from "@/types/BannerMode.interface";
import {
  Assignment,
  AssignmentConfig,
} from "@/types/models/Assignment.interface";
import { UserProperties } from "@/types/models/UserProperties.interface";
import useGetUserProperties from "@/users/v3/useGetUserProperties";
import { CONFIG_KEYS } from "@/view/AdditionalAssignmentSettings/AdditionalAssignmentSettings.constants";
import { Metadata } from "@/view/ContentHead";
import ContentLayout from "@/view/ContentLayout";
import ContentSettingsAdvisory from "@/view/ContentSettingsAdvisory";
import RelatedContent from "@/view/RelatedContent/RelatedContent";
import getAssignmentSetting from "@/view/utils/getAssignmentSetting";
import useAssignmentReducerContext from "@/view/utils/useAssignmentReducerContext";
import ViewPageFooter from "@/view/ViewPageFooter";
import GeniusActivityPanel from "./GeniusActivityPanel/GeniusActivityPanel";
import GeniusAssignActionsContainer from "./GeniusAssignActionsContainer";
import { GenerationGeniusContent } from "./GeniusContent.interface";
import { contentRootCss, mainSectionWrapperCss } from "./GeniusContent.style";
import GeniusContentHeader from "./GeniusContentHeader";
import GeniusEditorContent from "./GeniusEditorContent";
import GeniusVideo from "./GeniusVideo/GeniusVideo";
import { formatGeniusContentDocument } from "./utils";
import useGeniusEditor from "./utils/useGeniusEditor/useGeniusEditor";

export interface GeniusContentProps {
  bannerMode: BannerMode;
  content: GenerationGeniusContent;
}

const GeniusContent = ({ bannerMode, content }: GeniusContentProps) => {
  const contentData = content.data;
  const [duration, setDuration] = useState<string>("");
  const [showVideoOnly, setShowVideoOnly] = useState(true);

  const { wistia_id: wistiaId } = contentData;

  // Router
  const router = useRouter();
  const { query } = router;
  const { contentId, selectedAssignmentId } = query;

  const userProperties = useGetUserProperties() as UserProperties;
  const { isStudent, isTeacher } = userProperties;

  // Fetching assignments data
  const [fetchAssignment, assignmentRequest] = useLazyAssignmentGet();
  const { data: selectedAssignment, loading: isSelectedAssignmentLoading } =
    assignmentRequest;
  const { assignModal, dispatch, updatedAssignment } =
    useAssignmentReducerContext();
  const [postAssignmentViewRecord] = useAssignmentViewRecordCreate();

  // Fetching content metadata
  const {
    data: contentMetadata,
    error: contentMetadataRequestError,
    loading: contentMetadataLoading,
  }: UseRestQueryResponse<Metadata[]> = useContentMetadataGet(
    `${contentId}` || null,
  );

  useEffect(() => {
    // Fetch the current assignment if the selectedAssignmentId query param exists.
    fetchAssignment({
      id: selectedAssignmentId,
      queryParams: {
        content_member_id: contentId as string,
      },
      skip: !getIsSavedAssignment(selectedAssignmentId),
    });
  }, []);

  useEffect(() => {
    const shouldShowVideoOnly = getAssignmentSetting(
      CONFIG_KEYS.show_generation_genius_video_only as keyof AssignmentConfig,
      updatedAssignment,
    );
    setShowVideoOnly(isStudent && shouldShowVideoOnly);
  }, [isStudent, updatedAssignment]);

  // This fetches assignment data specific to the article content.
  const { data: userAssignments, loading: assignmentsLoading } =
    useAssignmentsGet({
      include_drafts: isTeacher,
      content_member_id: contentId as string,
    });
  const publishedUserAssignments = (
    (userAssignments as Assignment[]) || []
  ).filter((assignment) => !assignment.is_draft);

  const latestAssignment = getLatestAssignment(publishedUserAssignments);

  const currentAssignment = isStudent
    ? selectedAssignment || latestAssignment
    : selectedAssignment;

  useEffect(() => {
    recordStudentAssignmentView({
      isStudent,
      latestAssignment,
      publishedUserAssignments,
      postAssignmentViewRecord,
    });
  }, [JSON.stringify(publishedUserAssignments)]);

  const formattedContent = useMemo(
    () => formatGeniusContentDocument(contentData),
    [JSON.stringify(contentData)],
  );
  const editor = useGeniusEditor({ content: formattedContent });

  if (!editor) return null;

  return (
    <div css={contentRootCss}>
      <ContentSettingsAdvisory
        isStudent={isStudent}
        visibilityMarkers={content.visibility_markers}
      />
      <ContentLayout inline={false}>
        <ContentLayout.StickyBarContainer overrideToTop>
          <GeniusAssignActionsContainer
            assignmentRequest={assignmentRequest}
            assignModal={assignModal}
            bannerMode={bannerMode}
            content={content}
            currentAssignment={currentAssignment as Assignment}
            dispatch={dispatch}
            fetchAssignment={fetchAssignment}
            isSelectedAssignmentLoading={isSelectedAssignmentLoading}
            latestAssignment={latestAssignment}
            updatedAssignment={updatedAssignment as Assignment}
            userProperties={userProperties}
            router={router}
          />
        </ContentLayout.StickyBarContainer>
        <ContentLayout.Header>
          <GeniusVideo
            isVideoOnly={showVideoOnly}
            wistiaId={wistiaId}
            content={content}
            setDuration={setDuration}
          />
        </ContentLayout.Header>
        {!showVideoOnly && (
          <ContentLayout.Panel contentIsVideo>
            <GeniusActivityPanel
              assignments={publishedUserAssignments}
              assignmentsLoading={assignmentsLoading}
              content={content}
            />
          </ContentLayout.Panel>
        )}
        <ContentLayout.MainSection>
          <div css={mainSectionWrapperCss(showVideoOnly)}>
            <GeniusContentHeader
              content={content}
              duration={duration}
              bannerMode={bannerMode}
            />
            <GeniusEditorContent editor={editor} />
          </div>
        </ContentLayout.MainSection>
        <ContentLayout.PageBottom>
          {!contentMetadataRequestError && !contentMetadataLoading && (
            <RelatedContent
              content={content}
              contentMetadata={contentMetadata || []}
            />
          )}
          <ViewPageFooter />
        </ContentLayout.PageBottom>
      </ContentLayout>
    </div>
  );
};

export default GeniusContent;
